import { useMemo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BillingState,
  GetProgramEnrollmentPaymentPlansQuery,
  PaymentPlanStatus,
} from '@sandtech-hub-monorepo/ehub-client';

import { Button, cn, TimeUnit } from '@sandtech-hub-monorepo/ui-kit';
import { AlertCircle, CheckCircle, ChevronRight, Info } from 'lucide-react';
import { ExternalApplicationLinks } from '../../../../utils';
import { formatDate } from '../../../dateFormat';

export type AlertLevel = 'blue' | 'amber' | 'red' | 'success' | null;

// Context passed to message generator functions
export type PaymentMessageContext = {
  formattedDueDate: string;
  formattedOverdueDate: string;
  instalmentAmount: number;
  currentSemesterRemaining: number;
  previousSemesterRemaining: number;
  isAllAccess: boolean;
};

// Message generator function type
type MessageGenerator = (ctx: PaymentMessageContext) => ReactNode;

// Messages configuration type, mirroring the banner's decision tree:
// instalments already paid this semester → firstPaymentMade; otherwise
// beforeFirstWithOutstanding / beforeFirst depending on whether a previous
// semester balance is still owed, keyed by billing state severity.
export type PaymentBannerMessages = {
  success: ReactNode;
  fallback: {
    red: ReactNode;
    amber: ReactNode;
    blue: ReactNode;
  };
  firstPaymentMade?: {
    blue: MessageGenerator;
  };
  beforeFirstWithOutstanding: {
    blue: MessageGenerator;
    amber: MessageGenerator;
    red: MessageGenerator;
  };
  beforeFirst: {
    blue: MessageGenerator;
    amber: MessageGenerator;
    red: MessageGenerator;
  };
};

interface Props {
  paymentPlan?: GetProgramEnrollmentPaymentPlansQuery['viewer']['paymentPlans'][number];
  paymentFAQsLink: string;
  messages: PaymentBannerMessages;
  showPaymentPlanName?: boolean;
  showFAQLink?: boolean;
}

export const ProgramEnrollmentPaymentBanner = ({
  paymentPlan,
  paymentFAQsLink,
  messages,
  showPaymentPlanName = true,
  showFAQLink = true,
}: Props) => {
  const navigate = useNavigate();

  const { alertLevel, message, showBanner } = useMemo(() => {
    if (!paymentPlan) {
      return { showBanner: false };
    }
    if (
      !paymentPlan.status ||
      paymentPlan.status === PaymentPlanStatus.Cancelled
    ) {
      return { showBanner: false };
    }

    const {
      billingState,
      nextInstalmentDate,
      amountPerInstalment,
      currentSemesterPaidInstalments,
      currentSemesterRemainingAmount,
      previousSemesterRemainingAmount,
    } = paymentPlan;

    // If payment is completed or up to date, show success banner
    if (
      [
        BillingState.UpToDate,
        BillingState.InAdvance,
        BillingState.Completed,
      ].includes(billingState)
    ) {
      return {
        alertLevel: 'success' as AlertLevel,
        message: messages.success,
        showBanner: true,
      };
    }

    // Calculate due date from the plan's next instalment date. The payment
    // service no longer maintains invoices for SET/pathway plans, so the
    // banner must use the instalment date/amount instead of nextInvoice.
    const dueDate = nextInstalmentDate ? new Date(nextInstalmentDate) : null;
    if (!dueDate) {
      // Fallback to old logic if no due date
      const isError = [
        BillingState.InArrears,
        BillingState.Inactive,
        BillingState.Grace,
        BillingState.AtRisk,
        BillingState.Pending,
        BillingState.AwaitingPaymentConfirmation,
      ].includes(billingState);

      const level = isError ? 'red' : 'amber';
      return {
        alertLevel: level as AlertLevel,
        message: messages.fallback[level as 'red' | 'amber'],
        showBanner: true,
      };
    }
    const overdueDate = new Date(dueDate.valueOf() + 14 * TimeUnit.DAY);

    // Amounts come from the payment service in cents
    const instalmentAmount = (amountPerInstalment || 0) / 100;
    const currentSemesterRemaining =
      (currentSemesterRemainingAmount || 0) / 100;
    const previousSemesterRemaining =
      (previousSemesterRemainingAmount || 0) / 100;
    const ctx: PaymentMessageContext = {
      formattedDueDate: formatDate(dueDate),
      formattedOverdueDate: formatDate(overdueDate),
      instalmentAmount,
      currentSemesterRemaining,
      previousSemesterRemaining,
      isAllAccess: paymentPlan.code === 'all-access',
    };

    let level: AlertLevel = null;
    let bannerMessage: ReactNode = null;

    if (currentSemesterPaidInstalments) {
      if (currentSemesterRemaining && messages.firstPaymentMade) {
        level = 'blue';
        bannerMessage = messages.firstPaymentMade.blue(ctx);
      }
    } else if (previousSemesterRemaining) {
      if (billingState === BillingState.AtRisk) {
        level = 'red';
        bannerMessage = messages.beforeFirstWithOutstanding.red(ctx);
      } else if (billingState === BillingState.DueNow) {
        level = 'amber';
        bannerMessage = messages.beforeFirstWithOutstanding.amber(ctx);
      } else if (billingState === BillingState.DueSoon) {
        level = 'blue';
        bannerMessage = messages.beforeFirstWithOutstanding.blue(ctx);
      }
    } else {
      if (billingState === BillingState.AtRisk) {
        level = 'red';
        bannerMessage = messages.beforeFirst.red(ctx);
      } else if (billingState === BillingState.DueNow) {
        level = 'amber';
        bannerMessage = messages.beforeFirst.amber(ctx);
      } else if (billingState === BillingState.DueSoon) {
        level = 'blue';
        bannerMessage = messages.beforeFirst.blue(ctx);
      }
    }

    if (!level || !bannerMessage) {
      return { showBanner: false };
    }

    return {
      alertLevel: level,
      message: bannerMessage,
      showBanner: true,
    };
  }, [paymentPlan, messages]);

  if (!showBanner) {
    return null;
  }

  const isError = alertLevel === 'red';
  const isSuccess = alertLevel === 'success';
  const isInfo = alertLevel === 'blue';
  const isWarning = alertLevel === 'amber';

  return (
    <div
      className={cn(
        'px-4 py-3 lg:py-1 flex flex-col sm:flex-row gap-2 lg:gap-4 items-center border w-full rounded-lg',
        isError ? 'border-border-error bg-destructive/10 text-destructive' : '',
        isSuccess ? 'border-success bg-success/10 text-success' : '',
        isInfo ? 'border-info bg-info/10 text-info' : '',
        isWarning ? 'border-warning bg-warning/10 text-warning' : ''
      )}
    >
      {isError && <AlertCircle className="stroke-destructive h-6 w-6" />}
      {isSuccess && <CheckCircle className="stroke-success min-h-6 min-w-6" />}
      {(isInfo || isWarning) && (
        <Info className="stroke-current min-h-6 min-w-6" />
      )}
      {showPaymentPlanName && <b>{paymentPlan?.definition?.name}</b>}
      <div className="text-sm">
        {message}{' '}
        {showFAQLink && !isSuccess && paymentPlan && (
          <span>
            Read our{' '}
            <Button
              className={`h-fit p-0 font-semibold underline ${
                isError
                  ? 'text-error'
                  : isWarning
                  ? 'text-warning'
                  : 'text-primary'
              }`}
              variant={'link'}
              onClick={() => navigate(paymentFAQsLink)}
            >
              payments FAQ
            </Button>
            .
          </span>
        )}
      </div>
      {paymentPlan?.billingState !== BillingState.Completed && (
        <Button
          variant={
            isError
              ? 'ghostDestructive'
              : isInfo
              ? 'ghostInfo'
              : isWarning
              ? 'ghostWarning'
              : 'ghostSuccess'
          }
          className="mx-auto sm:ml-auto sm:mr-0 p-0"
          onClick={() =>
            window.location.replace(
              paymentPlan?.viewUrl || ExternalApplicationLinks.payments
            )
          }
        >
          {isError && (paymentPlan ? 'Pay now' : 'Activate')}
          {isSuccess && 'Make a Payment'}
          {(isInfo || isWarning) && 'Make payment now'}
          <ChevronRight />
        </Button>
      )}
    </div>
  );
};
